import {
  signal,
  inject,
  effect,
  ViewContainerRef,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import {
  SuiDialogBase,
  DialogService,
  DialogCloseReason,
  DialogCloseOptions,
  injectDirtyCloseHandler,
} from '@sui/dialog';

import { UnsavedChangesDialogComponent } from 'src/app/shared/components/unsaved-changes-dialog/unsaved-changes-dialog.component';

import {
  TabConfig,
  injectTabManager,
  TabSearchConfig,
  tabSearchManager,
} from '../utils';
import { DataObject, User } from 'src/app/core/models/core.models';

import { firstValueFrom } from 'rxjs';

/**
 * Base interface for tab dialog data.
 * Extend this in your dialog's TData interface to get validation control.
 */
export interface TabDialogDataBase {
  /**
   * If true, shows invalid tab indicators immediately on dialog open.
   * If false/undefined, validation errors only show after user interaction.
   * Use true for edit mode with potentially incomplete data.
   */
  validateFormsOnInit?: boolean;
}

/**
 * Base class for tab-based dialog components.
 *
 * Extends SuiDialogBase and adds tab management functionality.
 * Components extending this class get:
 * - typed DIALOG data access (from SuiDialogBase)
 * - TAB initialization and management
 * - Dirty State protection integration (opt-out via enableDirtyProtection = false)
 *
 * @template TData - the type of Data passed TO the dialog (via config.data)
 * @template TResult - the type of Data returned FROM the dialog (via close)
 */
export abstract class TabDialogBase<
  TData = unknown,
  TResult = unknown,
> extends SuiDialogBase<TData, TResult> {
  protected abstract tabHost: ViewContainerRef;
  protected cdr = inject(ChangeDetectorRef);
  protected tabManager = injectTabManager();
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);
  protected tabs: TabConfig[] = [];

  protected dialogService = inject(DialogService);
  protected translateService = inject(TranslateService);

  // * Set to [false] to disable dirty-close protection.
  // * Default: true (enabled for 99% of tab dialogs)
  protected enableDirtyProtection = true;

  // * Override to handle save when user chooses "Save & Close" on dirty dialog.
  // * formData -> combined FORM data from all TABS
  protected performSave?(formData: unknown): void | DialogCloseOptions<TResult>;

  // * Controls visibility of validation indicators in nav.
  // * Starts hidden for create mode, visible for edit mode.
  // * Once shown (via save attempt), stays visible.
  protected showValidation = signal(false);

  // * No results overlay element (managed automatically)
  private noResultsOverlay: HTMLElement | null = null;

  protected readonly host = inject(ElementRef<HTMLElement>).nativeElement;

  private noResultMessage = this.translateService.instant(
    'tab-dialog.no-results-message',
  );

  /**
   * Returns invalid tab IDs that should be visually indicated.
   * Respects showValidation signal (lazy validation visibility).
   */
  get visibleInvalidTabIds(): string[] {
    return this.showValidation() ? this.tabManager.getInvalidTabIds() : [];
  }

  /**
   * Returns a map of tab IDs to invalid field counts.
   * Respects showValidation signal (lazy validation visibility).
   */
  get visibleInvalidFieldCounts(): Record<string, number> {
    return this.showValidation() ? this.tabManager.getInvalidFieldCounts() : {};
  }

  // * filters TABS based on OBJECT-level permissions before initialization
  // ! call this BEFORE initializeBase() if you need permission-based TAB filtering
  protected filterTabsByObjectPermissions(
    object: DataObject,
    user: User,
  ): void {
    this.tabs = this.tabs.filter((tab) => {
      const hasPermsReq = !!tab.objectPermissions?.length;
      const hasLevelReq = tab.userAccessLevel != null;

      const permsOk = !hasPermsReq
        ? true
        : !!object &&
          tab.objectPermissions!.every((perm) => object[perm] === true);

      const levelOk = !hasLevelReq
        ? true
        : !!user && user.access_level_id >= tab.userAccessLevel!;

      return permsOk && levelOk;
    });
  }

  /**
   * Enables tab search functionality for this dialog
   * Automatically manages "no results" overlay
   * Usage: private tabSearch = this.enableTabSearch();
   */
  protected enableTabSearch(config?: TabSearchConfig) {
    const tabSearch = tabSearchManager(this.tabManager, config);

    // Auto-manage no results overlay
    effect(() => {
      const hasResults = tabSearch.hasVisibleTabs();

      if (!hasResults && !this.noResultsOverlay) {
        this.noResultsOverlay = this.createNoResultsOverlay();
      } else if (hasResults && this.noResultsOverlay) {
        this.noResultsOverlay.remove();
        this.noResultsOverlay = null;
      }
    });

    return tabSearch;
  }

  // * Creates the "no results" overlay element
  private createNoResultsOverlay(): HTMLElement {
    // ! below is imperative way to access the container and rely on the classes
    // ! but saves a lot of code in each child component and removes repititioin and complexity
    // ! some day might be refactored when we are in better position
    const container =
      this.host.querySelector('.dialog-content__left-pane') ||
      this.host.querySelector('.sui-dialog-tabs__right-content');

    if (!container) {
      return null;
    }

    const overlay = document.createElement('div');
    overlay.className = 'tab-search-no-results';
    overlay.innerHTML = `
      <div class="content">
        <p>${this.noResultMessage}</p>
      </div>
    `;

    container.appendChild(overlay);
    return overlay;
  }

  /**
   * Validates all forms and shows validation indicators.
   * Call this from save() methods before proceeding with save logic.
   * returns true if valid, false if invalid (save should be aborted)
   */
  protected validateAndShow(): boolean {
    // * Enable validation visibility from this point forward
    this.showValidation.set(true);

    const invalidIds = this.tabManager.getInvalidTabIds();

    if (invalidIds.length > 0) {
      // * Mark fields as touched to show field-level error messages
      this.tabManager.markAllAsTouched();
      return false;
    }

    return true;
  }

  // * call this in ngAfterViewInit() after the tabHost is available
  protected initializeBase(): void {
    this.tabManager.initialize(this.tabHost, this.tabs);
    this.tabManager.selectTab(this.tabs[0].id);

    setTimeout(() => this.cdr.detectChanges(), 0);

    // * Set initial validation visibility based on flag
    // * Edit mode (validateFormsOnInit: true) shows validation immediately
    // * Create mode (validateFormsOnInit: false/undefined) hides until save attempt
    const data = this.dialogData as TabDialogDataBase;
    const shouldValidateOnInit = data?.validateFormsOnInit ?? false;
    this.showValidation.set(shouldValidateOnInit);

    if (shouldValidateOnInit) {
      // * Delayed to ensure FormGroups are registered after change detection
      setTimeout(() => {
        this.tabManager.updateAllValidity();
        this.tabManager.markAllAsTouched();
      }, 0);
    }

    if (this.enableDirtyProtection) {
      this.setupDirtyHandler();
    }
  }

  // * Sets up dirty-close protection with unsaved changes dialog.
  // * Automatically validates, shows confirmation, and handles save flow.
  // ! if some custom flow:
  // ! set: protected override enableDirtyProtection = false;
  // ! create some customDirtyHandler OR do not create handler at all
  private setupDirtyHandler(): void {
    this.dirtyHandler.protectClosingWhenDirty(
      this.tabManager.isDirty,
      async () => {
        if (!this.tabManager.isDirty()) {
          return true;
        }

        // * Open unsaved changes dialog with 3 options
        const dialogRef = this.dialogService.openSimpleDialog(
          UnsavedChangesDialogComponent,
          {
            data: {
              isValid: this.tabManager.isValid,
            },
            closeOnClick: false,
          },
        );

        const confirmResult = await firstValueFrom(dialogRef.closed);

        if (confirmResult.reason === DialogCloseReason.SaveAndClose) {
          // * Validate and show indicators
          if (!this.validateAndShow()) {
            // ! don't close, validation failed
            return false;
          }

          // * Get form data and call child's save implementation
          const formData = this.tabManager.combineValues();
          const result = this.performSave?.(formData);

          // * Close with provided options or default Save reason
          const closeOptions: DialogCloseOptions<TResult> = result
            ? result
            : { reason: DialogCloseReason.Save, data: formData as TResult };
          this.dirtyHandler.closeImmediately(closeOptions);

          // ! Already closed via closeImmediately
          return false;
        }

        if (confirmResult.reason === DialogCloseReason.DiscardChanges) {
          // ! close without SAVE/UPDATE
          return true;
        }

        // * User chose Cancel, ESC, or backdrop click
        // ! don't close
        return false;
      },
    );
  }
}

// * TEMPLATE * //
/*
export class SOME_CUSTOM_COMPONENT_NAME
  extends TabDialogBase<DialogData, DialogResult>
  implements OnInit, AfterViewInit
{
  // * REQUIRED: ViewChild decorator must be in component class
  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  // * OPTIONAL: Only if you need subscriptions with takeUntilDestroyed()
  // protected destroyRef = inject(DestroyRef);

  // * OPTIONAL: Only if you need store/services
  // private store = inject(Store<AppState>);

  // * OPTIONAL: Only if implementing SearchableDialog
  // private tabSearch = this.enableTabSearch();
  // onSearchInput = this.tabSearch.onSearchInput;
  // get searchString$() { return this.tabSearch.searchString$; }
  // get visibleTabs(): TabConfig[] { return this.tabSearch.getVisibleTabs(this.tabs); }

  ngOnInit(): void {
    // this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    // this.tabSearch.setupAutoSwitch(this.tabs);
  }

  private setupTabs(): void {
    this.tabs = [
      {
        id: 'general',
        label: this.translateService.instant('...'),
        icon: faGear,
        component: SomeTabComponent,
        inputs: {},
      },
    ];
  }

  protected override performSave(formData: any): DialogCloseOptions<DialogResult> {
    // Implement save logic here
    return { reason: DialogCloseReason.Save, data: formData };
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
*/
